import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import { RootState } from "app/store";
import { getSelectedIds } from "utils/common";

interface AccountsReceivableState {
  selectedPrintPatientIds: Record<number, boolean>;
  openBulkPrintModal: boolean;
  openBulkEmailModal: boolean;
  openBulkPaymentRequestModal: boolean;
  openBulkSendToDarwillModal: boolean;
  showCheckboxes: boolean;
}

const initialState: AccountsReceivableState = {
  selectedPrintPatientIds: {},
  openBulkPrintModal: false,
  openBulkEmailModal: false,
  openBulkPaymentRequestModal: false,
  openBulkSendToDarwillModal: false,
  showCheckboxes: false
};

const accountsReceivableSlice = createSlice({
  name: "accounts-receivable",
  initialState,
  reducers: {
    addSelectedPrintPatientId: (
      state,
      action: PayloadAction<{ id: number; selected: boolean }>
    ) => {
      const { id, selected } = action.payload;
      state.selectedPrintPatientIds[id] = selected;
    },
    bulkSelectedPrintPatientId: (
      state,
      action: PayloadAction<{ ids: number[] }>
    ) => {
      const { ids } = action.payload;
      state.selectedPrintPatientIds = {};
      ids.forEach((id) => {
        state.selectedPrintPatientIds[id] = true;
      });
    },
    toggleOpenBulkPrintModal(
      state,
      action: PayloadAction<{ value: boolean | undefined }>
    ) {
      const { value } = action.payload;

      let newValue = !state.openBulkPrintModal;

      if (value !== undefined) {
        newValue = value;
      }
      state.openBulkPrintModal = newValue;

      if (!state.openBulkPrintModal) {
        state.openBulkEmailModal = false;
        state.openBulkPaymentRequestModal = false;
        state.openBulkSendToDarwillModal = false;
        state.showCheckboxes = false;
        state.selectedPrintPatientIds = {};
      }
    },
    toggleOpenBulkEmailModal(state) {
      const newValue = !state.openBulkEmailModal;
      state.openBulkEmailModal = newValue;
      state.openBulkPrintModal = newValue;

      if (!newValue) {
        state.showCheckboxes = false;
        state.selectedPrintPatientIds = {};
      }
    },
    toggleOpenBulkPaymentRequestModal(state) {
      const newValue = !state.openBulkPaymentRequestModal;
      state.openBulkPaymentRequestModal = newValue;
      state.openBulkPrintModal = newValue;

      if (!newValue) {
        state.showCheckboxes = false;
        state.selectedPrintPatientIds = {};
      }
    },
    toggleOpenBulkSendToDarwillModal(state) {
      const newValue = !state.openBulkSendToDarwillModal;
      state.openBulkSendToDarwillModal = newValue;
      state.openBulkPrintModal = newValue;

      if (!newValue) {
        state.showCheckboxes = false;
        state.selectedPrintPatientIds = {};
      }
    },
    toggleShowCheckboxes: (state) => {
      const newState = !state.showCheckboxes;

      state.showCheckboxes = newState;

      if (!newState) {
        state.selectedPrintPatientIds = {};
      }
    }
  }
});

export const selectSelectedPrintPatientIds = (state: RootState) =>
  state.accountsReceivable.selectedPrintPatientIds;

export const selectAccountsReceivableSelectionState = (
  state: RootState,
  patientsCount: number
) => {
  const selectedIds = getSelectedIds(
    state.accountsReceivable.selectedPrintPatientIds
  );
  if (selectedIds.length === 0) return "none";
  if (selectedIds.length === patientsCount) return "all";
  return "intermediate";
};

export const selectSelectedCount = (state: RootState) =>
  getSelectedIds(state.accountsReceivable.selectedPrintPatientIds).length;

export const selectOpenBulkPrintModal = (state: RootState) =>
  state.accountsReceivable.openBulkPrintModal;

export const selectOpenBulkEmailModal = (state: RootState) =>
  state.accountsReceivable.openBulkEmailModal;

export const selectOpenBulkPaymentRequestModal = (state: RootState) =>
  state.accountsReceivable.openBulkPaymentRequestModal;

export const selectOpenBulkSendToDarwillModal = (state: RootState) =>
  state.accountsReceivable.openBulkSendToDarwillModal;

export const selectShowCheckboxes = (state: RootState) =>
  state.accountsReceivable.showCheckboxes;

export const {
  addSelectedPrintPatientId,
  bulkSelectedPrintPatientId,
  toggleOpenBulkPrintModal,
  toggleOpenBulkEmailModal,
  toggleOpenBulkPaymentRequestModal,
  toggleOpenBulkSendToDarwillModal,
  toggleShowCheckboxes
} = accountsReceivableSlice.actions;

export default accountsReceivableSlice.reducer;
